import type { ChartDataPoint, HistoricSystemDataRecord, SystemEnergyHistoryDataItem } from '../types/charts';

import { addHours, addMinutes, formatHHmmInTz, fromUnixTime, minutesDiff, SITE_TIMEZONE } from './dateUtils';

/** Nominal spacing between consecutive half-hourly SOC samples (minutes). */
export const SOC_CHART_SLOT_MINUTES = 30;

/** Extra slack so minor clock skew does not fragment a continuous series. */
export const SOC_CHART_GAP_TOLERANCE_MINUTES = 5;

/** Gaps longer than this (minutes) start a new rendered segment. */
export const SOC_CHART_MAX_CONTIGUOUS_GAP_MINUTES = SOC_CHART_SLOT_MINUTES + SOC_CHART_GAP_TOLERANCE_MINUTES;

/** Minimum minutes between adjacent x-axis ticks so labels do not overlap (anywhere on the day). */
export const SOC_CHART_AXIS_TICK_MIN_GAP_MINUTES = 120;

/**
 * Processes the data for the battery state of charge chart.
 *
 * @param data - Array of historic system data records from the API
 * @param tz - IANA timezone for the inverter/site (e.g. "Europe/London"). Time labels are rendered in this timezone.
 * @param selectedDate - Calendar day in context (reserved for future filtering; x comes from each record)
 * @returns Points sorted by period start, with time labels for display in the site timezone
 */
export const processSocChartData = (
  data: HistoricSystemDataRecord[],
  tz: string = SITE_TIMEZONE,
  selectedDate?: Date
): ChartDataPoint[] => {
  void selectedDate;
  return data
    .map((record) => {
      const x = fromUnixTime(record.start_date);
      return {
        x,
        timeLabel: formatHHmmInTz(tz, x),
        y: parseInt(String(record.battery_soc), 10),
      };
    })
    .sort((a, b) => a.x.getTime() - b.x.getTime());
};

const pointTime = (p: ChartDataPoint): number => {
  const d = p.x instanceof Date ? p.x : new Date(p.x as unknown as string | number);
  return d.getTime();
};

/**
 * Splits SOC points into continuous runs so missing half-hours do not draw an interpolated bridge.
 */
export const splitSocChartDataIntoContinuousSegments = (points: ChartDataPoint[]): ChartDataPoint[][] => {
  if (points.length === 0) {
    return [];
  }

  const sorted = [...points].sort((a, b) => pointTime(a) - pointTime(b));
  const segments: ChartDataPoint[][] = [];
  let current: ChartDataPoint[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    const delta = minutesDiff(new Date(pointTime(curr)), new Date(pointTime(prev)));

    if (delta > SOC_CHART_MAX_CONTIGUOUS_GAP_MINUTES) {
      segments.push(current);
      current = [curr];
    } else {
      current.push(curr);
    }
  }

  segments.push(current);
  return segments;
};

/**
 * VictoryArea discards segments with fewer than two points (`getDataWithBaseline` in victory-area),
 * so a lone sample would show a tooltip (voronoi) but no stroke or fill. Duplicate the point to
 * the end of the nominal half-hour slot at the same SOC so the area renders as a flat segment.
 *
 * @param tz - IANA timezone for time label generation (should match the site timezone).
 */
export const padSocSegmentForVictoryArea = (
  segment: ChartDataPoint[],
  tz: string = SITE_TIMEZONE
): ChartDataPoint[] => {
  if (segment.length >= 2) {
    return segment;
  }
  if (segment.length === 0) {
    return [];
  }
  const p = segment[0];
  const xEnd = addMinutes(p.x, SOC_CHART_SLOT_MINUTES);
  return [p, { ...p, x: xEnd, timeLabel: formatHHmmInTz(tz, xEnd) }];
};

type SocAxisTickKind = 'major' | 'last' | 'dayEnd';

const SOC_AXIS_TICK_PRIORITY: Record<SocAxisTickKind, number> = {
  major: 3,
  last: 2,
  dayEnd: 1,
};

const isSameTickInstant = (a: Date, b: Date, msTolerance: number): boolean =>
  Math.abs(a.getTime() - b.getTime()) < msTolerance;

/**
 * Merges tick candidates on the same instant, keeping the higher-priority kind.
 */
const mergeSocAxisDuplicateInstants = (
  items: { t: Date; kind: SocAxisTickKind }[]
): { t: Date; kind: SocAxisTickKind }[] => {
  const sorted = [...items].sort((a, b) => a.t.getTime() - b.t.getTime());
  const out: { t: Date; kind: SocAxisTickKind }[] = [];
  for (const item of sorted) {
    const prev = out[out.length - 1];
    if (prev && isSameTickInstant(item.t, prev.t, 2 * 60 * 1000)) {
      if (SOC_AXIS_TICK_PRIORITY[item.kind] > SOC_AXIS_TICK_PRIORITY[prev.kind]) {
        out[out.length - 1] = item;
      }
    } else {
      out.push(item);
    }
  }
  return out;
};

/**
 * Removes lower-priority ticks until every adjacent pair is at least `SOC_CHART_AXIS_TICK_MIN_GAP_MINUTES` apart.
 */
const dedupeSocAxisTicksByMinGap = (items: { t: Date; kind: SocAxisTickKind }[]): Date[] => {
  const list = mergeSocAxisDuplicateInstants(items);
  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 0; i < list.length - 1; i += 1) {
      const gap = minutesDiff(list[i + 1].t, list[i].t);
      if (gap >= 0 && gap < SOC_CHART_AXIS_TICK_MIN_GAP_MINUTES) {
        const p0 = SOC_AXIS_TICK_PRIORITY[list[i].kind];
        const p1 = SOC_AXIS_TICK_PRIORITY[list[i + 1].kind];
        const removeIdx = p0 > p1 ? i + 1 : p0 < p1 ? i : i + 1;
        list.splice(removeIdx, 1);
        changed = true;
        break;
      }
    }
  }
  return list.map((x) => x.t);
};

/**
 * X-axis tick instants for the SOC chart: major UK hours, end of day, and last sample when they stay
 * at least {@link SOC_CHART_AXIS_TICK_MIN_GAP_MINUTES} apart so labels never crowd (e.g. 00:00 vs 00:30,
 * 18:00 vs last reading, 23:30 vs 23:59).
 */
export const buildSocChartXAxisTicks = (dayStart: Date, dayEnd: Date, lastPoint?: ChartDataPoint | null): Date[] => {
  const items: { t: Date; kind: SocAxisTickKind }[] = [0, 6, 12, 18].map((h) => ({
    t: addHours(dayStart, h),
    kind: 'major' as const,
  }));
  items.push({ t: dayEnd, kind: 'dayEnd' });

  if (lastPoint?.x) {
    const lastX = lastPoint.x instanceof Date ? lastPoint.x : new Date(lastPoint.x as unknown as string | number);
    const withinDay = lastX.getTime() >= dayStart.getTime() - 500 && lastX.getTime() <= dayEnd.getTime() + 500;
    if (withinDay) {
      const overlapsMajor = [0, 6, 12, 18].some((h) => isSameTickInstant(lastX, addHours(dayStart, h), 2 * 60 * 1000));
      if (!overlapsMajor) {
        items.push({ t: lastX, kind: 'last' });
      }
    }
  }

  return dedupeSocAxisTicksByMinGap(items);
};

/**
 * Processes the data for the system energy history chart
 *
 * @param data - Array of historic system data records from the API
 * @param tz - IANA timezone for the inverter/site. Time labels are rendered in this timezone.
 * @returns Array of processed data with time and energy values in kWh
 */
export const processSystemEnergyHistoryChartData = (
  data: HistoricSystemDataRecord[],
  tz: string = SITE_TIMEZONE
): SystemEnergyHistoryDataItem[] =>
  data.map((record) => ({
    time: formatHHmmInTz(tz, new Date(record.start_date * 1000)),
    'Solar generation': parseFloat(String(record.solar_generation / 1000)),
    Consumption: parseFloat(String(record.load_consumption / 1000)),
    Imported: parseFloat(String(record.imported / 1000)),
    Exported: -parseFloat(String(record.exported / 1000)),
  }));

/**
 * Calculates the y domain for the system energy history chart
 *
 * @param processedData - Array of processed system energy history data
 * @param setYDomain - State setter function for the y domain
 */
export const calculateYDomain = (
  processedData: SystemEnergyHistoryDataItem[],
  setYDomain: (domain: [number, number]) => void
): void => {
  let minY = 0;
  let maxY = 0;

  processedData.forEach((item) => {
    const sum = item['Solar generation'] + item.Consumption + item.Imported + item.Exported;
    minY = Math.min(minY, sum, item.Exported);
    maxY = Math.max(maxY, sum, item['Solar generation'], item.Consumption, item.Imported);
  });

  setYDomain([minY * 1.1, maxY * 1.1]);
};
